import { RuleModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RulesKeys, deleteRules } from '../../../api/rules-engine-api';
import { Dispatch, SetStateAction, useState } from 'react';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  RulesEngineRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteRuleDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  selectedRules: RuleModel[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  onSuccess?: () => void;
}

export default function DeleteRuleDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedRules,
  setSelectedRows,
  onSuccess,
}: DeleteRuleDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const ruleIds = selectedRules.map((rule) => rule.id);
  const moreThanOneRule = selectedRules.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteRules(ruleIds, changeLogMessage),
    onSuccess: () => {
      onSuccess?.();
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Automation rule${
          moreThanOneRule ? 's' : ''
        } deleted successfully!`,
      });
      setChangeLogMessage('');

      queryClient.setQueryData(
        [RulesKeys.RulesList],
        (oldData: RuleModel[]) => {
          if (!oldData) return [];
          return oldData.filter((rule) => !ruleIds.includes(rule.id));
        }
      );

      handleClose();
      setSelectedRows([]);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue deleting the selected automation rule${
          moreThanOneRule ? 's' : ''
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.rules_engine.delete_rule, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.rulesEngine,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, RulesEngineRoles.DeleteRules.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Delete Automation Rule${moreThanOneRule ? 's' : ''}`}
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            {moreThanOneRule
              ? `Do you wish to delete the ${selectedRules.length} selected automation rules?`
              : `Do you wish to delete the automation rule: "${selectedRules[0].basicInfo.ruleName}"?`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
